import { useEffect, useState } from "react";
import styles from "./FilterComponentNew.module.css";
import Image from "next/image";
import Post from "src/constants/Post";

// GTM tracking functions
const fireGTMFilterButtonClick = (buttonName) => {
  if (typeof window !== "undefined") {
    window.dataLayer.push({
      event: `${buttonName}ButtonClick`,
    });
  }
};

const fireGTMFilterApply = ({
  genres,
  retailers,
  price,
  rating,
  authorTitle = null,
  ageRange = null,
}) => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    // Clear previous filter values using _clear flag
    window.dataLayer.push({
      event: "FilterApply",
      "genre-filter": genres?.length > 0 ? genres : null,
      "retailer-filter": retailers?.length > 0 ? retailers : null,
      "price-filter": price > 0 ? `0 - ${price}` : 0,
      "rating-filter": rating > 0 ? `${rating} - 5` : null,
      "author-title-filter": authorTitle,
      "age-range-filter": ageRange,
      _clear: true,
    });
  }
};

const FilterComponentNew = ({
  selectedFilters,
  loading,
  setShowMenu,
  setSelectedFilters,
  setDefaultOpenAccordion,
  slug,
  postType,
}) => {
  const [searchText, setSearchText] = useState(selectedFilters.searchTerm || ""); // Initialize with selectedFilters.searchTerm
  const [loaderSize, setLoaderSize] = useState({ width: 1600, height: 5 });

  useEffect(() => {
    const updateSize = () => {
      setLoaderSize({ width: window.innerWidth, height: 5 });
    };

    updateSize(); // Set initial size
    window.addEventListener("resize", updateSize);

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    // Update searchText when selectedFilters.searchTerm changes
    setSearchText(selectedFilters.searchTerm || "");
  }, [selectedFilters.searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  const handleClearSearch = () => {
    if (searchText) {
      setSearchText(""); // Clear input

      // Update filters with null search term
      setSelectedFilters((prevState) => ({
        ...prevState,
        searchTerm: null, // Set to null when cleared
        isUserAppliedFilter: true,
      }));

      // Fire GTM FilterApply event with null author-title-filter
      fireGTMFilterApply({
        genres: selectedFilters.genres,
        retailers: selectedFilters.retailers,
        price: selectedFilters.maxPrice,
        rating: selectedFilters.rating,
        authorTitle: null, // Set to null when cleared
        ageRange: selectedFilters.ageRange,
      });
    }
  };

  const isAnyFilterSelected =
    (selectedFilters.genres && selectedFilters.genres.length > 0) ||
    (selectedFilters.retailers && selectedFilters.retailers.length > 0) ||
    (selectedFilters.maxPrice !== undefined && selectedFilters.maxPrice !== 5);

  const handleFilterButtonClick = (filterType, accordionType = "") => {
    // Fire GTM event
    fireGTMFilterButtonClick(filterType);
    // Update UI state
    setDefaultOpenAccordion(accordionType);
    setShowMenu(true);
  };

  const handleSearchSubmit = () => {
    // Update filters with search term
    setSelectedFilters((prevState) => ({
      ...prevState,
      searchTerm: searchText,
      isUserAppliedFilter: true,
    }));

    // Fire GTM FilterApply event with current filter values
    fireGTMFilterApply({
      genres: selectedFilters.genres,
      retailers: selectedFilters.retailers,
      price: selectedFilters.maxPrice,
      rating: selectedFilters.rating,
      authorTitle: searchText, // Pass searchText directly without null fallback
      ageRange: selectedFilters.ageRange,
    });
  };

  return (
    <>
      <section className={styles.filterNavbar}>
        <div className={styles.navContainer}>
          <div
            className={`${styles.dFlex} ${styles.alignItemsCenter} ${styles.justifyContentSpaceBetween} ${styles.filterFlex}`}
          >
            <div className={styles.filter}>
              <button
                className={`${styles.btnPrimary} ${!isAnyFilterSelected ? "" : styles.btnSecondary}`}
                type="button"
                onClick={() => handleFilterButtonClick("AllFilters", "")}
              >
                <span className={styles.btnInnerIcon}>
                  <Image src="/images/AllFilters-Icon.svg" alt="filter" width={20} height={20} />
                </span>
                <span className={styles.btnInnerText}>All Filters</span>
              </button>

              <button
                className={`${styles.btnPrimary} ${
                  !selectedFilters.genres || selectedFilters.genres.length === 0
                    ? styles.btnSecondary
                    : ""
                }`}
                type="button"
                onClick={() =>
                  handleFilterButtonClick(
                    slug === "deals/cheap-kids-books" ? "AgeFilter" : "GenreFilter",
                    slug === "deals/cheap-kids-books" ? "Age" : "Genres"
                  )
                }
              >
                <span className={styles.btnInnerIcon}>
                  <Image src="/images/Genre-Icon.svg" alt="genres" width={20} height={20} />
                </span>
                <span className={styles.btnInnerText}>
                  {slug === "deals/cheap-kids-books" ? "Age" : "Genres"}
                </span>
              </button>

              {postType !== Post.TYPES.KINDLE_UNLIMITED_DEALS && (
                <button
                  className={`${styles.btnPrimary} ${
                    !selectedFilters.retailers || selectedFilters.retailers.length === 0
                      ? styles.btnSecondary
                      : ""
                  }`}
                  type="button"
                  onClick={() => handleFilterButtonClick("RetailersFilter", "Retailers")}
                >
                  <span className={styles.btnInnerIcon}>
                    <Image src="/images/Retailer-Icon.svg" alt="retailer" width={20} height={20} />
                  </span>
                  <span className={styles.btnInnerText}>Retailers</span>
                </button>
              )}

              <button
                className={`${styles.btnPrimary} ${
                  selectedFilters.maxPrice === undefined || selectedFilters.maxPrice === 5
                    ? styles.btnSecondary
                    : ""
                }`}
                type="button"
                onClick={() => handleFilterButtonClick("PriceFilter", "Price")}
              >
                <span className={styles.btnInnerIcon}>
                  <Image src="/images/Price-Icon.svg" alt="price" width={20} height={20} />
                </span>
                <span className={styles.btnInnerText}>Price</span>
              </button>
            </div>

            <div className={styles.searchBox}>
              <div className={styles.search}>
                <input
                  type="text"
                  className={styles.searchTerm}
                  placeholder="Search by author or title"
                  value={searchText}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit();
                    }
                  }}
                />
                {searchText ? (
                  <img
                    src="/icons/close.svg"
                    alt="Clear"
                    width={16}
                    height={16}
                    className={styles.clearIcon}
                    onClick={handleClearSearch}
                  />
                ) : null}
                <button type="submit" className={styles.searchButton} onClick={handleSearchSubmit}>
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {loading ? (
        <Image
          src="/Progess-Bar.gif"
          className={styles.loader}
          alt="Animation"
          width={loaderSize.width}
          height={loaderSize.height}
        />
      ) : null}
    </>
  );
};

export default FilterComponentNew;
